import {
	type FloatingToolbarState,
	flip,
	offset,
	useFloatingToolbar,
	useFloatingToolbarState,
} from "@platejs/floating";
import { useEditorId, useEventEditorValue } from "platejs/react";
import type * as React from "react";
import { cn } from "@/lib/utils";

export function FloatingToolbar({
	children,
	className,
	state,
}: {
	children: React.ReactNode;
	className?: string;
	state?: FloatingToolbarState;
}) {
	const editorId = useEditorId();
	const focusedEditorId = useEventEditorValue("focus");

	const floatingToolbarState = useFloatingToolbarState({
		editorId,
		focusedEditorId,
		...state,
		floatingOptions: {
			middleware: [
				offset(12),
				flip({
					fallbackPlacements: ["top-start", "top-end", "bottom-start", "bottom-end"],
					padding: 12,
				}),
			],
			placement: "top",
			...state?.floatingOptions,
		},
	});

	const {
		clickOutsideRef,
		hidden,
		props: rootProps,
		ref: floatingRef,
	} = useFloatingToolbar(floatingToolbarState);

	if (hidden) return null;

	return (
		<div ref={clickOutsideRef}>
			<div
				ref={floatingRef as unknown as React.RefObject<HTMLDivElement>}
				role="toolbar"
				className={cn(
					"relative flex select-none items-center",
					"scrollbar-hide absolute z-50 overflow-x-auto whitespace-nowrap rounded-md border bg-popover p-1 opacity-100 shadow-md print:hidden",
					"max-w-[80vw]",
					className,
				)}
				style={rootProps.style}
			>
				{children}
			</div>
		</div>
	);
}
